import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  UsersThree, Plus, PencilSimple, Trash, Shield, X,
  User, EnvelopeSimple, Eye, EyeSlash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  project_team: 'Project Team',
  accounts: 'Accounts',
  viewer: 'Viewer',
  estimator: 'Estimator (Legacy)',
};

const ROLE_COLORS = {
  super_admin: 'bg-red-50 text-red-700 border-red-200',
  admin: 'bg-violet-50 text-violet-700 border-violet-200',
  project_team: 'bg-blue-50 text-blue-700 border-blue-200',
  accounts: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  viewer: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  estimator: 'bg-amber-50 text-amber-700 border-amber-200',
};

const STATUS_COLORS = {
  active: 'bg-green-50 text-green-700 border-green-200',
  inactive: 'bg-red-50 text-red-700 border-red-200',
};

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-sm border ${className}`}>{children}</span>
);

export const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permMatrix, setPermMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [permViewUser, setPermViewUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'project_team', reporting_manager_id: '', status: 'active' });

  const fetchAll = useCallback(async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/roles`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data.roles);
      setPermMatrix(rolesRes.data.permissions);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editingUser) {
        const payload = { name: form.name, role: form.role, status: form.status, reporting_manager_id: form.reporting_manager_id || null };
        if (form.password && form.password.length >= 6) payload.password = form.password;
        await axios.put(`${BACKEND_URL}/api/users/${editingUser.id}`, payload, { withCredentials: true });
        toast.success('User updated');
      } else {
        if (!form.email.trim()) { toast.error('Email is required'); return; }
        if (!form.password || form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
        await axios.post(`${BACKEND_URL}/api/auth/register`, {
          email: form.email, password: form.password, name: form.name, role: form.role,
        }, { withCredentials: true });
        toast.success('User created');
      }
      setModalOpen(false);
      setEditingUser(null);
      setForm({ name: '', email: '', password: '', role: 'project_team', reporting_manager_id: '', status: 'active' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save user'); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, reporting_manager_id: u.reporting_manager_id || '', status: u.status || 'active' });
    setModalOpen(true);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-500">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <UsersThree size={24} weight="fill" className="text-violet-500" />
              <div>
                <h1 className="text-xl font-bold tracking-tight text-zinc-900" data-testid="user-mgmt-title">User Management</h1>
                <p className="text-xs text-zinc-500 mt-0.5">Manage users, roles, and permissions</p>
              </div>
            </div>
            <Button onClick={() => { setEditingUser(null); setForm({ name: '', email: '', password: '', role: 'project_team', reporting_manager_id: '', status: 'active' }); setModalOpen(true); }}
              className="bg-zinc-900 text-white rounded-none text-xs" data-testid="add-user-btn">
              <Plus size={16} className="mr-1.5" /> Add User
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Role Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {roles.map(role => {
            const count = users.filter(u => u.role === role).length;
            return (
              <div key={role} className="bg-white border border-zinc-200 rounded-sm p-3" data-testid={`role-card-${role}`}>
                <div className="text-2xl font-bold text-zinc-900">{count}</div>
                <div className="text-[11px] text-zinc-500 font-medium mt-0.5">{ROLE_LABELS[role] || role}</div>
              </div>
            );
          })}
        </div>

        {/* Users Table */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-[10px] text-zinc-500 uppercase tracking-wide border-b border-zinc-200">
                <th className="px-4 py-2.5 text-left">User</th>
                <th className="px-4 py-2.5 text-left">Email</th>
                <th className="px-4 py-2.5 text-left">Role</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-left">Reports To</th>
                <th className="px-4 py-2.5 text-left">Since</th>
                <th className="px-4 py-2.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-zinc-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-600">
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-zinc-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500 text-xs">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={ROLE_COLORS[u.role] || 'bg-zinc-100 text-zinc-600 border-zinc-200'}>
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge className={STATUS_COLORS[u.status] || STATUS_COLORS['active']}>
                      {u.status || 'active'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500">{u.reporting_manager_name || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-zinc-400">{new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1 justify-center">
                      <button onClick={() => setPermViewUser(permViewUser?.id === u.id ? null : u)} className="p-1 hover:bg-violet-50 text-violet-500" title="View Permissions" data-testid={`perms-${u.email}`}>
                        <Shield size={14} />
                      </button>
                      <button onClick={() => openEdit(u)} className="p-1 hover:bg-zinc-100 text-zinc-500" title="Edit" data-testid={`edit-${u.email}`}>
                        <PencilSimple size={14} />
                      </button>
                      {u.id !== currentUser?.id && (
                        <button onClick={() => handleDelete(u.id)} className="p-1 hover:bg-red-50 text-red-500" title="Delete" data-testid={`delete-${u.email}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Permission Matrix View */}
        {permViewUser && (
          <div className="mt-4 bg-white border border-zinc-200 rounded-sm p-4" data-testid="perm-matrix">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shield size={16} className="text-violet-500" />
                <span className="text-sm font-bold text-zinc-900">Permissions — {permViewUser.name}</span>
                <Badge className={ROLE_COLORS[permViewUser.role]}>{ROLE_LABELS[permViewUser.role] || permViewUser.role}</Badge>
              </div>
              <button onClick={() => setPermViewUser(null)}><X size={16} className="text-zinc-400" /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="px-2 py-1.5 text-left text-[10px] text-zinc-500 uppercase">Module</th>
                    {['view', 'create', 'edit', 'delete', 'approve', 'override'].map(action => (
                      <th key={action} className="px-2 py-1.5 text-center text-[10px] text-zinc-500 uppercase">{action}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(permMatrix[permViewUser.role] || {}).map(([mod, perms]) => (
                    <tr key={mod} className="border-b border-zinc-50">
                      <td className="px-2 py-1.5 font-medium text-zinc-700">{mod}</td>
                      {['view', 'create', 'edit', 'delete', 'approve', 'override'].map(action => (
                        <td key={action} className="px-2 py-1.5 text-center">
                          {perms.includes(action) ? (
                            <span className="inline-block w-4 h-4 rounded-full bg-green-500 text-white text-[9px] leading-4">&#10003;</span>
                          ) : (
                            <span className="inline-block w-4 h-4 rounded-full bg-zinc-200 text-zinc-400 text-[9px] leading-4">&#10005;</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-white w-full max-w-md rounded-sm shadow-xl" onClick={e => e.stopPropagation()} data-testid="user-modal">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200">
              <h2 className="text-sm font-bold text-zinc-900">{editingUser ? 'Edit User' : 'Add User'}</h2>
              <button onClick={() => setModalOpen(false)}><X size={18} className="text-zinc-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Name *</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className="mt-1 rounded-none border-zinc-300 h-9 text-sm" data-testid="user-name-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Email *</label>
                <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  disabled={!!editingUser}
                  className="mt-1 rounded-none border-zinc-300 h-9 text-sm disabled:bg-zinc-100" data-testid="user-email-input" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </label>
                <div className="relative mt-1">
                  <Input type={showPassword ? 'text' : 'password'} value={form.password}
                    onChange={e => setForm({...form, password: e.target.value})}
                    className="rounded-none border-zinc-300 h-9 text-sm pr-9" data-testid="user-password-input" />
                  <button onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-2 text-zinc-400">
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Role *</label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                    className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="user-role-select">
                    {roles.filter(r => currentUser?.role === 'super_admin' || r !== 'super_admin').map(r => (
                      <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                    className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="user-status-select">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Reporting Manager</label>
                <select value={form.reporting_manager_id} onChange={e => setForm({...form, reporting_manager_id: e.target.value})}
                  className="mt-1 w-full border border-zinc-300 px-2 py-2 text-sm" data-testid="user-manager-select">
                  <option value="">None</option>
                  {users.filter(u => u.id !== editingUser?.id).map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role] || u.role})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <Button variant="outline" onClick={() => setModalOpen(false)} className="rounded-none border-zinc-300 text-xs">Cancel</Button>
              <Button onClick={handleSave} className="bg-zinc-900 text-white rounded-none text-xs" data-testid="save-user-btn">{editingUser ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
